/**
 * CSV generation for the nametag export.
 *
 * Two things this has to get right:
 *
 *  1. Quoting. Names contain commas ("Smith, Jr"), apostrophes and occasionally
 *     line breaks pasted in from elsewhere. RFC 4180 quoting handles all three.
 *
 *  2. Formula injection. A field beginning with =, +, -, @, tab or carriage
 *     return is executed as a formula when the file is opened in Excel, Sheets
 *     or Numbers. Attendee names come from Eventbrite — that is, from whatever
 *     a member of the public typed into a registration form — so a name like
 *     `=HYPERLINK("http://evil","click")` would otherwise run on the organiser's
 *     machine. Prefixing with a single quote neutralises it while displaying
 *     the original text.
 */

/** Characters that make a spreadsheet treat a cell as a formula. */
const FORMULA_PREFIXES = ['=', '+', '-', '@', '\t', '\r']

export function escapeCsvField(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return ''

  let text = String(value)

  if (FORMULA_PREFIXES.some((prefix) => text.startsWith(prefix))) {
    text = `'${text}`
  }

  if (/[",\r\n]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`
  }

  return text
}

export interface CsvColumn<T> {
  header: string
  value: (row: T) => string | number | null | undefined
}

export function toCsv<T>(rows: readonly T[], columns: readonly CsvColumn<T>[]): string {
  const lines = [columns.map((column) => escapeCsvField(column.header)).join(',')]

  for (const row of rows) {
    lines.push(columns.map((column) => escapeCsvField(column.value(row))).join(','))
  }

  // CRLF per RFC 4180 — Excel on Windows needs it, everything else tolerates it.
  return lines.join('\r\n')
}

/**
 * Triggers a download in the browser.
 *
 * The BOM is what makes Excel read the file as UTF-8; without it, an attendee
 * called Zoë appears as Zoë on a Windows machine.
 */
export function downloadCsv(filename: string, csv: string): void {
  // U+FEFF: without the byte-order mark Excel reads the file as Latin-1
  // and an attendee called Zoë shows up as ZoÃ«.
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)

  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)

  URL.revokeObjectURL(url)
}

/** Filesystem-safe filename stem from an event name. */
export function csvFilename(eventName: string, suffix: string): string {
  const stem =
    eventName
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60) || 'event'

  return `${stem}-${suffix}.csv`
}
